export const FORM_WIDTH = 640;

export enum CustomerType {
  Individual,
  Ira,
  Joint,
}

export enum IraAccountType {
  TraditionalIra,
  RolloverIra,
  SepIra, // not supported yet
  RothIra,
  SimpleIraParticipant,
}

export enum JointAccountType {
  JointRightsOfSurvivorship,
}

export enum EmploymentStatus {
  Employed,
  Retired,
  Student,
  Unemployed,
}

export enum TimeHorizon {
  Short,
  Average,
  Longest,
}

export enum LiquidityNeeds {
  VeryImportant,
  SomewhatImportant,
  NotImportant,
}

export enum ESigned {
  Yes,
  No,
}

export enum InvestmentExperience {
  None,
  Limited,
  Good,
  Extensive,
}

export enum InvestmentObjective {
  GrowthIncome,
  Growth,
  Income,
  CapitalPreservation,
  Speculation,
  MaximumGrowth,
  Balanced,
  Other,
}

export enum RiskTolerance {
  Low,
  Medium,
  High,
}

export enum Relationship {
  Spouse,
  Children,
  GrandChildren,
  Other,
}

export enum IsPoliticallyExposed {
  Yes,
  No,
}

export enum AccountStatus {
  Draft,
  Submitted,
  Pending,
  ActionRequired,
  Opened,
  Error,
  Cancelled,
  Suspended,
  Rejected,
}

export interface ApplicantDTO {
  legalFirstName: string | null;
  legalLastName: string | null;
  streetAddressLine1: string | null;
  streetAddressLine2: string | null;
  city: string | null;
  postalCode: string | null;
  state: string | null;
  phoneNumber: string | null;
  email: string | null;
  ssn: string | null;
  dob: string | null;
  employmentStatus: EmploymentStatus | null;
  employerName: string | null;
  positionEmployed: string | null;
}

export interface InterestedPartyDTO {
  legalFirstName: string | null;
  legalLastName: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface TrustedContactDTO {
  legalFirstName: string | null;
  legalLastName: string | null;
  email: string | null;
  country: string | null;
}

export interface RetirementAccountBeneficiaryDTO {
  givenName: string;
  familyName: string;
  dateOfBirth: string;
  socialSecurityNumber: string;
  streetAddress: string;
  city: string;
  postalCode: string;
  relationship: Relationship | null;
  state: string | null;
}

export interface DraftAccountDTO {
  accountIraType: IraAccountType | null;
  annualIncomeUSDDefault: number | null;
  annualIncomeUSDMax: number | null;
  annualIncomeUSDMin: number | null;
  brokerDealerfirmName: string | null;
  companySymbols: string[] | null;
  customerType: CustomerType | null;
  eSigned: ESigned | null;
  immediateFamily: string | null;
  interestedPartyInfo: InterestedPartyDTO | null;
  investmentExperience: InvestmentExperience | null;
  investmentObjective: InvestmentObjective | null;
  isAffiliatedExchangeOrFINRA: boolean;
  isControlPerson: boolean | null;
  isCustodianEmployee: boolean | null;
  isPoliticallyExposed: IsPoliticallyExposed;
  jointAccountType: JointAccountType | null;
  liquidNetWorthUSDDefault: number | null;
  liquidNetWorthUSDMax: number | null;
  liquidNetWorthUSDMin: number | null;
  liquidityNeeds: LiquidityNeeds | null;
  politicalOrganization: string | null;
  primaryApplicant: ApplicantDTO;
  retirementAccountBeneficiaryInfo: RetirementAccountBeneficiaryDTO | null;
  riskTolerance: RiskTolerance | null;
  secondApplicant: ApplicantDTO;
  simpleIraPlanName: string | null;
  snapIds: string[];
  timeHorizon: TimeHorizon | null;
  totalNetWorthUSDDefault: number | null;
  totalNetWorthUSDMax: number | null;
  totalNetWorthUSDMin: number | null;
  trustedContactInfo: TrustedContactDTO | null;
}

interface OptionsDTO {
  allowedConsumerTypes: CustomerType[];
  allowedIraAccountTypes: IraAccountType[];
}

export interface AccountOpeningDraftDTO {
  id: string | null;
  data: DraftAccountDTO;
  accountOpeningStatus: AccountStatus;
  options: OptionsDTO;
}

export const stepsMap: Record<CustomerType, string[]> = {
  [CustomerType.Individual]: ['Personal Info', 'Employment', 'Account', 'Review'],
  [CustomerType.Joint]: ['Personal Info', "Partner's Info", 'Employment', 'Account', 'Review'],
  [CustomerType.Ira]: ['Personal Info', 'Employment', 'Account', 'Beneficiary', 'Review'],
};

export const yesNoOpts = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

export const DOB_FE_FORMAT = 'MM/DD/YYYY';
export const DOB_BE_FORMAT = 'YYYY-MM-DD';

export interface FormValues {
  primaryApplicant: ApplicantDTO & { employmentStatus: EmploymentStatus };
  secondApplicant: ApplicantDTO & { employmentStatus: EmploymentStatus };

  retirementAccountBeneficiaryInfo: RetirementAccountBeneficiaryDTO & { hasBeneficiary: boolean };

  isControlPerson: boolean;
  companySymbols: string;

  isCustodianEmployee: boolean;

  isAffiliatedExchangeOrFINRA: boolean;
  brokerDealerfirmName: string | null;
  snapId: string | null;

  isPoliticallyExposed: boolean;
  immediateFamily: string | null;
  politicalOrganization: string | null;

  liquidityNeeds: LiquidityNeeds;

  timeHorizon: TimeHorizon;

  riskTolerance: RiskTolerance | null;

  investmentExperience: InvestmentExperience | null;

  investmentObjective: InvestmentObjective;

  trustedContactInfo: TrustedContactDTO & { hasTrustedContact: boolean };

  interestedPartyInfo: InterestedPartyDTO & { hasInterestedParty: boolean };

  eSigned: boolean | null;
}
